import { getSubdomain } from '../org'
import { authStore, type AuthUser } from '../auth/store'

/**
 * same-origin 상대경로. 프로덕션은 LB 가 /api/* 를 서버로 보내고, 로컬은 vite proxy 가 대신한다.
 * cross-origin(api.biznomi.com) 을 쓰지 않는 이유 — 구형 WebView(Chrome 79~83) 의 CORS 버그로
 * 프리플라이트가 조용히 죽어 로그인이 멈추던 사례가 있었다. same-origin 은 프리플라이트가 없다.
 */
/*
 * 경로 분리 — 이 앱은 `app.biznomi.com/mtt/` 아래에 산다 (2026-09-04).
 *
 * 호출부는 전부 `/api/...` 를 넘긴다. 그래서 여기에 붙는 값은 **`/mtt`** 다.
 * `/mtt/api` 로 두면 `/mtt/api/api/...` 가 된다.
 *
 * LB 가 `/mtt/api/*` 를 server-mtt 로 보내되 **경로는 안 자른다** (classic LB 라
 * urlRewrite 를 못 쓴다). 서버가 `app.use('/mtt', routes)` 로 같이 받는다.
 */
const API_BASE = '/mtt'

export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
  /**
   * 서버가 주는 기계 판독용 코드. 문구로 분기하면 문구가 바뀔 때 조용히 깨진다.
   * 예) 'ABSENCE_SAME_DAY' — 같은 날 부재 중복. 하드 차단이 아니라 확인 신호다.
   */
  code?: string
  /** HTTP status — 오프라인 큐 sync 가 일시 실패(5xx·429·401)와 영구 실패(4xx)를 구분하는 데 쓴다 */
  status?: number
}

async function safeJson<T>(res: Response): Promise<ApiResponse<T>> {
  try {
    const json = (await res.json()) as ApiResponse<T>
    return { ...json, status: res.status }
  } catch {
    return { success: false, error: `서버 오류 (${res.status})`, status: res.status }
  }
}

function baseHeaders(extra?: HeadersInit): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Subdomain': getSubdomain(),
    ...extra,
  }
}

// ── 토큰 갱신 ────────────────────────────────────────────────
// 동시에 여러 요청이 401 을 받아도 refresh 는 한 번만 나가야 한다.
let refreshPromise: Promise<string | null> | null = null

async function tryRefreshToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const rt = authStore.get().refreshToken
      if (!rt) return null

      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: baseHeaders(),
        body: JSON.stringify({ refreshToken: rt }),
      })
      const json = (await res.json()) as ApiResponse<{
        accessToken: string
        refreshToken: string
        user?: AuthUser
      }>

      if (!json.success || !json.data) return null

      const { accessToken, refreshToken, user } = json.data
      if (user) authStore.get().setAuth({ accessToken, refreshToken }, user)
      else authStore.get().setTokens({ accessToken, refreshToken })

      return accessToken
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// ── 인증 요청 ────────────────────────────────────────────────

/**
 * 로그인한 회사와 지금 보고 있는 회사가 어긋났을 때 무엇을 해야 하는지 알려 준다.
 *
 * 🔴 이 상황은 실제로 생긴다 (2026-09-08 실측). 토큰은 메모리에, 회사 코드는
 *   localStorage 에 있어서 **탭을 두 개 열고 한쪽에서 다른 회사로 로그인하면**
 *   먼저 열어 둔 탭이 계속 403 을 받는다. 서버는 막아 주는데(authMiddleware 가
 *   토큰 조직과 헤더 조직을 대조한다) 화면은 이유를 안 알려 줘서 «오류» 만 반복했다.
 *
 * ⚠ 여기서 `clearAuth()` 를 부르면 안 된다. 토큰은 브라우저에 하나뿐이라,
 *   지우면 **방금 로그인한 쪽**이 끊긴다. 고장난 탭 하나 때문에 멀쩡한 탭을
 *   로그아웃시키는 셈이다. 새로고침하면 그 탭이 현재 회사로 맞춰진다.
 */
function withTenantHint<T>(res: ApiResponse<T>): ApiResponse<T> {
  if (res.status === 403 && res.error?.includes('다른 조직')) {
    return {
      ...res,
      error: '다른 회사 계정으로 로그인되어 있습니다. 새로고침하거나 다시 로그인해 주세요.',
    }
  }
  return res
}

/** 인증이 필요한 요청. 401 이면 토큰을 갱신해 한 번 재시도하고, 그래도 실패하면 로그아웃. */
export async function api<T = unknown>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const token = authStore.get().accessToken
  const send = (bearer: string | null) =>
    fetch(`${API_BASE}${path}`, {
      ...options,
      headers: baseHeaders({
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        ...options?.headers,
      }),
    })

  const res = await send(token)
  if (res.status !== 401) return withTenantHint(await safeJson<T>(res))

  const fresh = await tryRefreshToken()
  if (fresh) {
    const retry = await send(fresh)
    return withTenantHint(await safeJson<T>(retry))
  }

  authStore.get().clearAuth()
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
  return { success: false, error: '세션이 만료되었습니다. 다시 로그인해 주세요.', status: 401 }
}

/**
 * 파일 올리기 (multipart). api() 를 못 쓰는 이유는 Content-Type 이다.
 *
 * ⚠️ FormData 를 보낼 때 Content-Type 을 직접 넣으면 안 된다. multipart 헤더에는
 *    본문 구분자(boundary)가 들어가야 하는데 그건 브라우저만 안다. 손으로
 *    'multipart/form-data' 만 넣으면 boundary 가 빠져 서버가 본문을 못 읽는다.
 *    그래서 baseHeaders() 를 쓰지 않고 필요한 헤더만 직접 고른다.
 */
export async function apiForm<T = unknown>(path: string, form: FormData): Promise<ApiResponse<T>> {
  const send = (bearer: string | null) =>
    fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: {
        'X-Subdomain': getSubdomain(),
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: form,
    })

  const res = await send(authStore.get().accessToken)
  if (res.status !== 401) return safeJson<T>(res)

  const fresh = await tryRefreshToken()
  if (fresh) return safeJson<T>(await send(fresh))

  authStore.get().clearAuth()
  return { success: false, error: '세션이 만료되었습니다. 다시 로그인해 주세요.', status: 401 }
}

/**
 * 파일 내려받기. 엑셀은 JSON 이 아니라 바이너리라 api() 를 쓸 수 없다.
 *
 * URL 로 그냥 이동시키지 않는 이유 — 엔드포인트가 Authorization 헤더를 요구하므로
 * 주소창 이동으로는 401 이 난다. 토큰을 쿼리스트링에 실으면 서버 로그·브라우저
 * 히스토리에 액세스 토큰이 남는다.
 *
 * ⚠️ a 태그를 DOM 에 붙였다 뗀다. 안 붙이면 일부 브라우저에서 click() 이 무시된다.
 *    revoke 도 지연시킨다 — 즉시 해제하면 저장이 시작되기 전에 blob 이 사라져
 *    다운로드가 조용히 취소되는 경우가 있다.
 */
export async function download(path: string, filename: string): Promise<string | null> {
  const send = (bearer: string | null) =>
    fetch(`${API_BASE}${path}`, {
      headers: baseHeaders({ ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) }),
    })

  let res = await send(authStore.get().accessToken)
  if (res.status === 401) {
    const fresh = await tryRefreshToken()
    if (!fresh) {
      authStore.get().clearAuth()
      return '세션이 만료되었습니다. 다시 로그인해 주세요.'
    }
    res = await send(fresh)
  }
  if (!res.ok) {
    const json = await res.json().catch(() => null)
    return (json as { error?: string } | null)?.error ?? `내려받기에 실패했습니다 (${res.status})`
  }

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return null
}

/**
 * 바이너리를 화면에서 열려고 받아올 때 (근로계약서 PDF 등).
 *
 * download() 와 다른 점 — 파일로 저장시키지 않고 blob 을 그대로 돌려준다.
 * 계약서는 "받아서 보관"이 아니라 "그 자리에서 읽고 서명"이 기본 동작이다.
 */
export async function fetchBlob(path: string): Promise<{ blob: Blob } | { error: string }> {
  const send = (bearer: string | null) =>
    fetch(`${API_BASE}${path}`, {
      headers: baseHeaders({ ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) }),
    })

  let res = await send(authStore.get().accessToken)
  if (res.status === 401) {
    const fresh = await tryRefreshToken()
    if (!fresh) {
      authStore.get().clearAuth()
      return { error: '세션이 만료되었습니다. 다시 로그인해 주세요.' }
    }
    res = await send(fresh)
  }
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: string } | null
    return { error: json?.error ?? `파일을 열지 못했습니다 (${res.status})` }
  }
  return { blob: await res.blob() }
}

/** 인증 없이 부르는 요청 (브랜딩·로그인 등) */
export async function publicApi<T = unknown>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers: baseHeaders(options?.headers) })
  return safeJson<T>(res)
}
